import { Switch as SwitchPrimitive } from 'radix-ui';

import { cn } from '../../lib/utils';
import { cva } from 'class-variance-authority';

const switchVariants = cva<SwitchVariant>('', {
  variants: {
    size: {
      default: 'h-6 w-11',
      sm: 'h-4 w-7',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const thumbVariants = cva<ThumbVariant>('', {
  variants: {
    size: {
      default: 'h-5 w-5 data-[state=checked]:translate-x-5',
      sm: 'h-3 w-3 data-[state=checked]:translate-x-3',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitive.Root
> & {
  size?: Size;
};

function Switch({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & {
  size?: 'sm' | 'default';
}) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        'peer inline-flex shrink-0 cursor-pointer items-center',
        'rounded-full border-2 border-transparent transition-colors',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary-blue-600 data-[state=unchecked]:bg-slate-200',
        'dark:focus-visible:ring-slate-300 dark:focus-visible:ring-offset-primary-blue-600 dark:data-[state=checked]:bg-slate-50 dark:data-[state=unchecked]:bg-primary-blue-400',
        switchVariants({ size }),
        className,
      )}
      {...props}
      data-slot="switch"
      data-size={size}>
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block rounded-full',
          'bg-white shadow-lg ring-0 dark:bg-primary-blue-600',
          'transition-transform data-[state=unchecked]:translate-x-0',
          thumbVariants({ size }),
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };

export type Size = 'default' | 'sm';

export type SwitchVariant = {
  size: Record<Size, string>;
};

export type ThumbVariant = SwitchVariant;
